/**
 * PURE option builder for the unified Scatter & Bubble family.
 *
 * No Angular / no ECharts-runtime dependency — takes capabilities + flat rows and
 * returns a plain EChartsOption, so all variant logic is unit-testable. The
 * component is just a lifecycle shell around `buildScatterBubbleOption`.
 *
 * Variant → behaviour (driven by config `variantFlags`, NOT name strings):
 *   scatter        → single series of [x,y] points.
 *   multi-scatter  → one series per `series` value (legend), each its own color.
 *   bubble         → points sized by `sizeField`, scaled between symbolSizeMin/Max.
 *   quadrant       → scatter + 2 mean split lines + 4 background quadrant bands.
 *   (regression / correlation / cluster / bubble-matrix → declared in config;
 *    render as plain scatter for now — advanced math deferred.)
 *
 * EVERY styling constant is read from `capabilities.chartDefaults` (sourced from
 * JSON `scatterBubbleChartDefaults`). The FALLBACK object below is a last-resort
 * safety net ONLY (so the pure builder runs standalone in tests / with no config);
 * edit visuals in the JSON, never here.
 */
import type { EChartsOption } from 'echarts';
import { toCapitalCase } from '../../utils/text-case.utils';
import type { ScatterBubbleCapabilities } from './unified-scatter-bubble-chart.types';

type Row = Record<string, unknown>;

/** LAST-RESORT safety net ONLY — the authoritative values live in the JSON
 *  (`scatterBubbleChartDefaults`). Used solely when both the per-tile capability
 *  AND the config are missing, so a broken/absent config never throws. */
const FALLBACK = {
  palette: ['#1f6feb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'],
  symbolSize: 12,
  symbolSizeMin: 8,
  symbolSizeMax: 48,
  pointOpacity: 0.8,
  valueAxisTicks: 5,
  rotateThreshold: 6,
  autoRotateDegrees: 30,
  quadrantColors: ['rgba(16,185,129,0.06)', 'rgba(31,111,235,0.06)', 'rgba(239,68,68,0.06)', 'rgba(245,158,11,0.06)'],
  quadrantLineColor: '#94a3b8',
  quadrantLabelColor: '#64748b',
  quadrantLabelFontSize: 11,
  referenceLineColor: '#64748b',
  regressionLineColor: '#8b5cf6',
  regressionLineWidth: 2,
  regressionBandSteps: 40,
  confidenceZ: 1.96,
  confidenceBandOpacity: 0.12,
  correlationGradient: ['#fde047', '#16a34a'],
  visualMapStyle: { itemWidth: 14, itemHeight: 110, fontSize: 10, textHigh: 'High', textLow: 'Low' },
  clusterHullOpacity: 0.15,
  multiScatterSymbols: ['circle', 'triangle', 'rect', 'diamond', 'roundRect', 'arrow'],
  statTextColor: '#475569',
  statFontSize: 12,
  labelFontSize: 11,
  labelColor: '#475569',
  layout: { gridLeft: 12, gridRight: 20, gridRightLegend: 72, gridTop: 28, gridTopLegend: 36, gridBottomBase: 16, gridBottomLegend: 30, gridBottomAxisName: 22, valueNameGap: 26, endNameGap: 8, visualMapGutter: 56, legendRightOffset: 4, statInsetX: 8, statInsetY: 4, statLineGap: 5, tooltipGapNear: 12, tooltipGapFar: 18, tooltipFixedInset: 8 },
  tooltip: { seriesColorFallback: '#1f6feb' },
};

/** Numeric coercion tolerant of "1,234"/"$1.2k" → numeric part; non-numeric → null
 *  (null lets us DROP a point that has no valid X/Y rather than plot it at 0). */
function toNum(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const n = Number(value.replace(/[^0-9.eE+-]/g, ''));
    return Number.isFinite(n) && value.trim() !== '' ? n : null;
  }
  return null;
}

/** Year-like dimension: every value an integer in 1900–2100 (the repo-wide
 *  year-column convention) — rendered as plain "2024", never "2,024.5". */
function isYearDimension(vals: number[]): boolean {
  return vals.length > 0 && vals.every((v) => Number.isInteger(v) && v >= 1900 && v <= 2100);
}

/** Display labels for series-group values: ISO date-times shrink to the
 *  SHORTEST form that keeps every group distinct — date only ("2026-06-26"),
 *  then + hh:mm, + seconds, + milliseconds (T/Z stripped throughout). Groups
 *  never collapse into one legend entry; non-date values pass through as-is. */
function prettyGroupLabels(values: string[]): string[] {
  const isIso = (v: string) => /^\d{4}-\d{2}-\d{2}[T ]/.test(v);
  if (!values.some(isIso)) return values;
  // Cut points into "YYYY-MM-DD HH:mm:ss.SSS": date / minutes / seconds / ms.
  for (const cut of [10, 16, 19, 23]) {
    const labels = values.map((v) => (isIso(v) ? v.slice(0, cut).replace('T', ' ') : v));
    if (new Set(labels).size === labels.length) return labels;
  }
  return values;
}

/** Distinct values of a column, in first-seen order. */
function distinct(rows: Row[], field: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  rows.forEach((row) => {
    const key = String(row[field] ?? '');
    if (!seen.has(key)) { seen.add(key); out.push(key); }
  });
  return out;
}

/** Lightweight number formatter (kept local so the builder has no Angular dep). */
function formatValue(value: number, capabilities: ScatterBubbleCapabilities): string {
  if (!Number.isFinite(value)) return '-';
  const scale = capabilities.numberScale || 'none';
  const fmt = capabilities.numberFormat || 'NONE';
  const decimals = Math.max(0, Math.min(6, Math.floor(capabilities.decimals ?? 1)));
  let scaled = value;
  let suffix = '';
  switch (scale) {
    case 'thousands': case 'SCALE_THOUSANDS': scaled = value / 1e3; suffix = 'K'; break;
    case 'millions': case 'MILLIONS': scaled = value / 1e6; suffix = 'M'; break;
    case 'billions': case 'BILLIONS': scaled = value / 1e9; suffix = 'B'; break;
    case 'compact': case 'COMPACT': {
      const abs = Math.abs(value);
      if (abs >= 1e9) { scaled = value / 1e9; suffix = 'B'; }
      else if (abs >= 1e6) { scaled = value / 1e6; suffix = 'M'; }
      else if (abs >= 1e3) { scaled = value / 1e3; suffix = 'K'; }
      break;
    }
    default: break;
  }
  if (fmt === 'PERCENT' || fmt === 'percent') return `${(value * 100).toFixed(decimals)}%`;
  const useCommas = fmt === 'THOUSANDS' || fmt === 'thousands' || suffix === '';
  const body = useCommas && !suffix
    ? scaled.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: decimals })
    : scaled.toFixed(decimals);
  return `${body}${suffix}`;
}

/** Map a tooltip position preset to an ECharts `tooltip.position` value.
 *  Pixel gaps come from config layout (tooltipGapNear/Far, tooltipFixedInset). */
function tooltipPositionFor(preset: string | undefined, gap: { near: number; far: number; inset: number }): unknown {
  switch (preset) {
    case 'top': return (point: number[], _p: unknown, _dom: unknown, _r: unknown, size: { contentSize: number[] }) => [point[0] - size.contentSize[0] / 2, point[1] - size.contentSize[1] - gap.near];
    case 'bottom': return (point: number[], _p: unknown, _dom: unknown, _r: unknown, size: { contentSize: number[] }) => [point[0] - size.contentSize[0] / 2, point[1] + gap.far];
    case 'left': return (point: number[], _p: unknown, _dom: unknown, _r: unknown, size: { contentSize: number[] }) => [point[0] - size.contentSize[0] - gap.near, point[1] - size.contentSize[1] / 2];
    case 'right': return (point: number[], _p: unknown, _dom: unknown, _r: unknown, size: { contentSize: number[] }) => [point[0] + gap.far, point[1] - size.contentSize[1] / 2];
    case 'fixed-tr': return (_pt: unknown, _p: unknown, _dom: unknown, _r: unknown, size: { viewSize: number[]; contentSize: number[] }) => [size.viewSize[0] - size.contentSize[0] - gap.inset, gap.inset];
    case 'follow':
    case 'auto':
    default: return undefined;
  }
}

/** A prepared point: [x, y] or [x, y, size], plus the raw row for tooltips. */
interface PreparedPoint {
  value: number[];
  name: string;
  /** Source row (first row of the group when aggregated) — feeds the
   *  `tooltipColumns` extras on hover. */
  row: Row;
}

// ── Overlay math (pure, deterministic — no Math.random so resume/tests are stable) ──

/** Ordinary-least-squares linear fit y = a + b·x, plus R² and the moments the
 *  confidence band needs. Null when fewer than 2 distinct points. */
function linearFit(pts: Array<[number, number]>):
  | { a: number; b: number; r2: number; n: number; meanX: number; sxx: number; syy: number }
  | null {
  const n = pts.length;
  if (n < 2) return null;
  const meanX = pts.reduce((s, p) => s + p[0], 0) / n;
  const meanY = pts.reduce((s, p) => s + p[1], 0) / n;
  let sxx = 0;
  let syy = 0;
  let sxy = 0;
  pts.forEach(([x, y]) => {
    sxx += (x - meanX) * (x - meanX);
    syy += (y - meanY) * (y - meanY);
    sxy += (x - meanX) * (y - meanY);
  });
  if (sxx === 0) return null;
  const b = sxy / sxx;
  const a = meanY - b * meanX;
  const r2 = syy === 0 ? 1 : (sxy * sxy) / (sxx * syy);
  return { a, b, r2, n, meanX, sxx, syy };
}

/** Pearson correlation coefficient of paired samples (NaN-safe → 0). */
function pearson(pts: Array<[number, number]>): number {
  const fit = linearFit(pts);
  if (!fit) return 0;
  const r = Math.sign(fit.b) * Math.sqrt(Math.max(0, Math.min(1, fit.r2)));
  return Number.isFinite(r) ? r : 0;
}

/** Ranks with average ties — turns Spearman into Pearson-on-ranks. */
function ranks(values: number[]): number[] {
  const order = values.map((v, i) => ({ v, i })).sort((p, q) => p.v - q.v);
  const out = new Array<number>(values.length);
  let k = 0;
  while (k < order.length) {
    let j = k;
    while (j + 1 < order.length && order[j + 1].v === order[k].v) j++;
    const avgRank = (k + j) / 2 + 1;
    for (let m = k; m <= j; m++) out[order[m].i] = avgRank;
    k = j + 1;
  }
  return out;
}

/** K-means on [x, y] (min-max normalized), k clusters. DETERMINISTIC init:
 *  centroids seeded from points evenly spaced through the x-sorted order.
 *  Returns per-point cluster assignments. */
function kMeansAssign(pts: number[][], k: number): number[] {
  const n = pts.length;
  if (!n) return [];
  const kk = Math.max(1, Math.min(k, n));
  const min = [Infinity, Infinity];
  const max = [-Infinity, -Infinity];
  pts.forEach((p) => {
    for (let d = 0; d < 2; d++) {
      if (p[d] < min[d]) min[d] = p[d];
      if (p[d] > max[d]) max[d] = p[d];
    }
  });
  const norm = (p: number[]): [number, number] => [
    max[0] === min[0] ? 0 : (p[0] - min[0]) / (max[0] - min[0]),
    max[1] === min[1] ? 0 : (p[1] - min[1]) / (max[1] - min[1]),
  ];
  const np = pts.map(norm);
  const sortedIdx = np.map((_, i) => i).sort((a, b) => np[a][0] - np[b][0] || np[a][1] - np[b][1]);
  let centroids: Array<[number, number]> = Array.from({ length: kk }, (_, c) => {
    const pick = sortedIdx[Math.min(n - 1, Math.floor(((c + 0.5) * n) / kk))];
    return [np[pick][0], np[pick][1]];
  });
  let assign = new Array<number>(n).fill(0);
  for (let iter = 0; iter < 12; iter++) {
    let moved = false;
    assign = np.map((p, i) => {
      let best = 0;
      let bestDist = Infinity;
      centroids.forEach((c, ci) => {
        const dist = (p[0] - c[0]) ** 2 + (p[1] - c[1]) ** 2;
        if (dist < bestDist) { bestDist = dist; best = ci; }
      });
      if (best !== assign[i]) moved = true;
      return best;
    });
    const sums = Array.from({ length: kk }, () => [0, 0, 0]);
    np.forEach((p, i) => {
      const s = sums[assign[i]];
      s[0] += p[0]; s[1] += p[1]; s[2]++;
    });
    centroids = centroids.map((c, ci) => (sums[ci][2] ? [sums[ci][0] / sums[ci][2], sums[ci][1] / sums[ci][2]] : c));
    if (!moved) break;
  }
  return assign;
}

/** Convex hull (Andrew monotone chain) of [x, y] points, CCW, no duplicates. */
function convexHull(pts: Array<[number, number]>): Array<[number, number]> {
  const sorted = [...pts].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (sorted.length < 3) return sorted;
  const cross = (o: number[], p: number[], q: number[]) => (p[0] - o[0]) * (q[1] - o[1]) - (p[1] - o[1]) * (q[0] - o[0]);
  const lower: Array<[number, number]> = [];
  sorted.forEach((p) => {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  });
  const upper: Array<[number, number]> = [];
  [...sorted].reverse().forEach((p) => {
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  });
  lower.pop();
  upper.pop();
  return [...lower, ...upper];
}

/**
 * Build the EChartsOption for a unified scatter / bubble chart from flat rows.
 * Returns `{ series: [] }` when the config is incomplete (no X or no Y) so the
 * caller can still render an (empty) chart safely.
 */
export function buildScatterBubbleOption(rows: Array<Record<string, unknown>>, capabilities: ScatterBubbleCapabilities): EChartsOption {
  const safeRows = Array.isArray(rows) ? rows : [];
  const xField = capabilities.xField;
  const yField = capabilities.yField;
  if (!xField || !yField) return { series: [] };

  // Render dispatch is config-driven: variantFlags first, variant name as fallback.
  // Flags are INDEPENDENT so overlays compose with any base variant
  // (Quadrant + Bubble, Regression + Multi Scatter, …).
  const vf = capabilities.variantFlags;
  const isBubble = vf ? vf.bubble === true : capabilities.variant === 'bubble';
  const isMulti = vf ? vf.multiSeries === true : capabilities.variant === 'multi-scatter';
  const isQuadrant = vf ? vf.quadrant === true : capabilities.variant === 'quadrant';
  const isRegression = vf ? vf.regression === true : capabilities.variant === 'regression';
  const isCorrelation = vf ? vf.correlation === true : capabilities.variant === 'correlation';
  const isCluster = vf ? vf.cluster === true : capabilities.variant === 'cluster';
  /** Bubble Matrix: categorical X × Y grid; bubble size = the measure rolled up
   *  per cell. Overlays keep working because points become [xIdx, yIdx, size] —
   *  numeric pairs the quadrant/regression/correlation/cluster math runs on. */
  const isMatrix = vf ? vf.bubbleMatrix === true : capabilities.variant === 'bubble-matrix';
  const sizeField = isBubble && capabilities.sizeField ? capabilities.sizeField : '';
  const seriesField = isMulti && capabilities.series ? capabilities.series : '';
  const colorField = capabilities.colorField || '';

  // ── Aggregation: roll up rows sharing the same X (+ series/color group) with
  //    the chosen function on Y and Size. 'none' (default) plots every raw row.
  //    The MATRIX does its own per-cell rollup instead (X and Y are categories
  //    there — collapsing by X alone would destroy the Y dimension). ──
  const agg = capabilities.aggregation && capabilities.aggregation !== 'none' ? capabilities.aggregation : '';
  let workRows = safeRows;
  if (agg && !isMatrix) {
    const buckets = new Map<string, { row: Row; ys: number[]; sizes: number[] }>();
    safeRows.forEach((row) => {
      const x = toNum(row[xField]);
      if (x === null) return;
      const key = `${x}|${seriesField ? String(row[seriesField] ?? '') : ''}|${colorField ? String(row[colorField] ?? '') : ''}`;
      let bucket = buckets.get(key);
      if (!bucket) { bucket = { row: { ...row }, ys: [], sizes: [] }; buckets.set(key, bucket); }
      const y = toNum(row[yField]);
      if (y !== null) bucket.ys.push(y);
      if (sizeField) {
        const s = toNum(row[sizeField]);
        if (s !== null) bucket.sizes.push(s);
      }
    });
    const reduce = (vals: number[]): number | null => {
      if (agg === 'count') return vals.length;
      if (!vals.length) return null;
      switch (agg) {
        case 'sum': return vals.reduce((a, b) => a + b, 0);
        case 'avg': return vals.reduce((a, b) => a + b, 0) / vals.length;
        case 'min': return Math.min(...vals);
        case 'max': return Math.max(...vals);
        default: return vals[0];
      }
    };
    workRows = [...buckets.values()].map((bucket) => {
      const out = { ...bucket.row };
      const y = reduce(bucket.ys);
      out[yField] = y === null ? '' : y; // '' → toNum null → point dropped
      if (sizeField) {
        const s = reduce(bucket.sizes);
        out[sizeField] = s === null ? '' : s;
      }
      return out;
    });
  }

  // ── Config is the source of truth (d = scatterBubbleChartDefaults from JSON) ──
  const d = capabilities.chartDefaults || {};
  const cfgPalette = d.palette?.length ? d.palette : FALLBACK.palette;
  const palette = capabilities.paletteColors?.length ? capabilities.paletteColors : cfgPalette;
  const symbolSize = capabilities.symbolSize ?? d.symbolSize ?? FALLBACK.symbolSize;
  const sizeMin = capabilities.symbolSizeMin ?? d.symbolSizeMin ?? FALLBACK.symbolSizeMin;
  const sizeMax = capabilities.symbolSizeMax ?? d.symbolSizeMax ?? FALLBACK.symbolSizeMax;
  const pointOpacity = capabilities.pointOpacity ?? d.pointOpacity ?? FALLBACK.pointOpacity;
  const valueAxisTicks = d.valueAxisTicks ?? FALLBACK.valueAxisTicks;
  const quadrantColors = d.quadrantColors?.length ? d.quadrantColors : FALLBACK.quadrantColors;
  const quadrantLineColor = capabilities.quadrantLineColor || d.quadrantLineColor || FALLBACK.quadrantLineColor;
  const quadrantLabelColor = d.quadrantLabelColor || FALLBACK.quadrantLabelColor;
  const quadrantLabelFontSize = d.quadrantLabelFontSize ?? FALLBACK.quadrantLabelFontSize;
  const referenceLineColor = d.referenceLineColor || FALLBACK.referenceLineColor;
  // Overlay knobs — per-tile capability first, then chartDefaults, then FALLBACK.
  const regressionLineColor = capabilities.regressionLineColor || d.regressionLineColor || FALLBACK.regressionLineColor;
  const regressionLineWidth = d.regressionLineWidth ?? FALLBACK.regressionLineWidth;
  const regressionBandSteps = d.regressionBandSteps ?? FALLBACK.regressionBandSteps;
  const confidenceZ = d.confidenceZ ?? FALLBACK.confidenceZ;
  const vmStyle = { ...FALLBACK.visualMapStyle, ...(d.visualMapStyle || {}) };
  const confidenceBandOpacity = capabilities.confidenceBandOpacity ?? d.confidenceBandOpacity ?? FALLBACK.confidenceBandOpacity;
  const cfgGradient = d.correlationGradient?.length === 2 ? d.correlationGradient : FALLBACK.correlationGradient;
  const gradientLow = capabilities.correlationGradientStart || cfgGradient[0];
  const gradientHigh = capabilities.correlationGradientEnd || cfgGradient[1];
  const clusterHullOpacity = capabilities.clusterHullOpacity ?? d.clusterHullOpacity ?? FALLBACK.clusterHullOpacity;
  const multiSymbols = d.multiScatterSymbols?.length ? d.multiScatterSymbols : FALLBACK.multiScatterSymbols;
  const statTextColor = d.statTextColor || FALLBACK.statTextColor;
  const statFontSize = d.statFontSize ?? FALLBACK.statFontSize;
  const labelFontSize = capabilities.labelFontSize ?? d.labelFontSize ?? FALLBACK.labelFontSize;
  const labelColor = capabilities.labelColor || d.labelColor || FALLBACK.labelColor;

  const L = (d.layout || {}) as Record<string, number | undefined>;
  const lx = (key: string, fallback: number): number => (typeof L[key] === 'number' ? (L[key] as number) : fallback);
  const gridLeft = lx('gridLeft', FALLBACK.layout.gridLeft);
  const gridRight = lx('gridRight', FALLBACK.layout.gridRight);
  const gridRightLegend = lx('gridRightLegend', FALLBACK.layout.gridRightLegend);
  const gridTop = lx('gridTop', FALLBACK.layout.gridTop);
  const gridTopLegend = lx('gridTopLegend', FALLBACK.layout.gridTopLegend);
  const gridBottomBase = lx('gridBottomBase', FALLBACK.layout.gridBottomBase);
  const gridBottomLegend = lx('gridBottomLegend', FALLBACK.layout.gridBottomLegend);
  const gridBottomAxisName = lx('gridBottomAxisName', FALLBACK.layout.gridBottomAxisName);
  const valueNameGap = lx('valueNameGap', FALLBACK.layout.valueNameGap);
  const endNameGap = lx('endNameGap', FALLBACK.layout.endNameGap);
  const visualMapGutter = lx('visualMapGutter', FALLBACK.layout.visualMapGutter);
  const legendRightOffset = lx('legendRightOffset', FALLBACK.layout.legendRightOffset);
  const statInsetX = lx('statInsetX', FALLBACK.layout.statInsetX);
  const statInsetY = lx('statInsetY', FALLBACK.layout.statInsetY);
  const statLineGap = lx('statLineGap', FALLBACK.layout.statLineGap);
  const tooltipGapNear = lx('tooltipGapNear', FALLBACK.layout.tooltipGapNear);
  const tooltipGapFar = lx('tooltipGapFar', FALLBACK.layout.tooltipGapFar);
  const tooltipFixedInset = lx('tooltipFixedInset', FALLBACK.layout.tooltipFixedInset);

  // ── Prepare points. Drop rows with a non-numeric X or Y so a bad cell doesn't
  //    plant a phantom point at the origin. Bubble adds the size dimension. ──
  const preparePoint = (row: Row): PreparedPoint | null => {
    const x = toNum(row[xField]);
    const y = toNum(row[yField]);
    if (x === null || y === null) return null;
    const value = sizeField ? [x, y, toNum(row[sizeField]) ?? 0] : [x, y];
    return { value, name: colorField ? String(row[colorField] ?? '') : '', row };
  };

  // ── Matrix grid prep: distinct sorted categories on each axis; one point per
  //    occupied cell at [xIdx, yIdx, cellValue] where cellValue = the Size
  //    measure rolled up per cell (row count when no Size is mapped). ──
  let xCats: string[] = [];
  let yCats: string[] = [];
  let allPts: PreparedPoint[];
  if (isMatrix) {
    const sortCats = (vals: string[]): string[] => {
      const nums = vals.map((v) => toNum(v));
      return nums.every((n) => n !== null)
        ? [...vals].sort((a, b) => (toNum(a) as number) - (toNum(b) as number))
        : [...vals].sort((a, b) => a.localeCompare(b));
    };
    xCats = sortCats(distinct(workRows, xField));
    yCats = sortCats(distinct(workRows, yField));
    const cellAgg = agg || 'sum';
    const cells = new Map<string, { xi: number; yi: number; vals: number[]; row: Row }>();
    workRows.forEach((row) => {
      const xi = xCats.indexOf(String(row[xField] ?? ''));
      const yi = yCats.indexOf(String(row[yField] ?? ''));
      if (xi < 0 || yi < 0) return;
      const key = `${xi}|${yi}`;
      let cell = cells.get(key);
      if (!cell) { cell = { xi, yi, vals: [], row }; cells.set(key, cell); }
      if (sizeField) {
        const s = toNum(row[sizeField]);
        if (s !== null) cell.vals.push(s);
      } else {
        cell.vals.push(1);
      }
    });
    const reduceCell = (vals: number[]): number => {
      if (!sizeField || cellAgg === 'count') return vals.length;
      if (!vals.length) return 0;
      switch (cellAgg) {
        case 'avg': return vals.reduce((a, b) => a + b, 0) / vals.length;
        case 'min': return Math.min(...vals);
        case 'max': return Math.max(...vals);
        case 'sum':
        default: return vals.reduce((a, b) => a + b, 0);
      }
    };
    allPts = [...cells.values()].map((cell) => ({
      value: [cell.xi, cell.yi, reduceCell(cell.vals)],
      name: colorField ? String(cell.row[colorField] ?? '') : '',
      row: cell.row,
    }));
  } else {
    allPts = workRows.map(preparePoint).filter((p): p is PreparedPoint => !!p);
  }

  // Bubble size scaling: map the raw size value into [sizeMin, sizeMax] px.
  // The matrix ALWAYS has a size dimension (the per-cell value at index 2).
  const sizeDimActive = !!sizeField || isMatrix;
  let sizeLo = 0;
  let sizeHi = 1;
  if (sizeDimActive) {
    const sizes = allPts.map((p) => p.value[2] ?? 0);
    sizeLo = Math.min(...sizes, 0);
    sizeHi = Math.max(...sizes, 1);
  }
  const scaleSize = (raw: number): number => {
    if (!sizeDimActive || sizeHi === sizeLo) return symbolSize;
    const t = (raw - sizeLo) / (sizeHi - sizeLo);
    return sizeMin + t * (sizeMax - sizeMin);
  };
  const symbolSizeFn = sizeDimActive
    ? (val: number[]) => scaleSize(val[2] ?? 0)
    : symbolSize;

  // ── Build series. Cluster → k-means pockets; multi-scatter → one series per
  //    `series` value; else one. Cluster takes precedence over the Series
  //    grouping (algorithmic pockets ARE the grouping). ──
  interface SbSeries { name: string; type: 'scatter'; data: Array<number[] | { value: number[]; srcRow: Row }>; symbolSize: unknown; symbol?: string; itemStyle: Record<string, unknown>; label?: unknown; markLine?: unknown; markArea?: unknown; }
  const seriesList: SbSeries[] = [];
  // Overlay series appended AFTER the legend snapshots seriesList (trend line,
  // confidence band, cluster hulls — none of them legend entries).
  const extraSeries: Array<Record<string, unknown>> = [];
  const graphics: Array<Record<string, unknown>> = [];

  const pointLabel = capabilities.showValues === true
    ? {
        show: true,
        position: capabilities.labelPosition || 'top',
        fontSize: labelFontSize,
        color: labelColor,
        // Matrix labels show the per-cell value; scatter/bubble labels show Y.
        formatter: (p: { value?: number[] }) => formatValue(Number(p?.value?.[isMatrix ? 2 : 1] ?? 0), capabilities),
      }
    : undefined;

  // Extra tooltip columns ride on each data item as `srcRow` (only when asked
  // for — plain value arrays otherwise, keeping the option lean).
  const tooltipCols = (capabilities.tooltipColumns || []).filter((c) => c && c !== xField && c !== yField && c !== sizeField);

  const mkSeries = (name: string, pts: PreparedPoint[], color: string, symbol?: string): SbSeries => ({
    name,
    type: 'scatter',
    data: tooltipCols.length ? pts.map((p) => ({ value: p.value, srcRow: p.row })) : pts.map((p) => p.value),
    symbolSize: symbolSizeFn,
    ...(symbol ? { symbol } : {}),
    itemStyle: { color, opacity: pointOpacity },
    ...(pointLabel ? { label: pointLabel } : {}),
  });

  if (isCluster && allPts.length) {
    // K-means pockets on X/Y — one colored series per cluster (+ optional hull).
    const k = Math.max(2, Math.min(8, Math.floor(capabilities.clusterCount ?? 3)));
    const assign = kMeansAssign(allPts.map((p) => p.value), k);
    const clusterPts: PreparedPoint[][] = Array.from({ length: k }, () => []);
    allPts.forEach((p, i) => clusterPts[assign[i]].push(p));
    clusterPts.forEach((pts, c) => {
      if (pts.length) seriesList.push(mkSeries(`Cluster ${c + 1}`, pts, palette[c % palette.length]));
    });
    if (capabilities.showClusterHulls !== false) {
      // Faint convex hull wrapped around each pocket, drawn under the points.
      const hulls = clusterPts.map((pts) => convexHull(pts.map((p) => [p.value[0], p.value[1]] as [number, number])));
      extraSeries.push({
        type: 'custom',
        silent: true,
        z: 1,
        data: hulls.map((_, c) => c),
        tooltip: { show: false },
        renderItem: (params: { dataIndex: number }, api: { coord: (v: number[]) => number[] }) => {
          const hull = hulls[params.dataIndex];
          if (!hull || hull.length < 3) return null;
          return {
            type: 'polygon',
            silent: true,
            shape: { points: hull.map((pt) => api.coord(pt)) },
            style: { fill: palette[params.dataIndex % palette.length], opacity: clusterHullOpacity },
          };
        },
      });
    }
  } else if (seriesField && !isMatrix) {
    // Multi Scatter: one series per group — distinct color AND marker shape
    // (config multiScatterSymbols) so series stay tellable in monochrome print.
    const groups = distinct(workRows, seriesField);
    const groupLabels = prettyGroupLabels(groups);
    groups.forEach((g, i) => {
      const pts = workRows.filter((r) => String(r[seriesField] ?? '') === g).map(preparePoint).filter((p): p is PreparedPoint => !!p);
      seriesList.push(mkSeries(groupLabels[i] || '(blank)', pts, palette[i % palette.length], multiSymbols[i % multiSymbols.length]));
    });
  } else {
    seriesList.push(mkSeries(toCapitalCase(capabilities.yLabel || yField), allPts, palette[0]));
  }

  // ── Quadrant: mean split lines + 4 background bands (all config-colored). ──
  if (isQuadrant && seriesList.length) {
    // Means from the prepared points — numeric X/Y for scatter/bubble, category
    // INDICES for the matrix (so the split lands mid-grid, weighted by cells).
    const allX = allPts.map((p) => p.value[0]);
    const allY = allPts.map((p) => p.value[1]);
    const meanX = allX.length ? allX.reduce((a, b) => a + b, 0) / allX.length : 0;
    const meanY = allY.length ? allY.reduce((a, b) => a + b, 0) / allY.length : 0;
    const first = seriesList[0] as SbSeries;
    first.markLine = {
      silent: true,
      symbol: 'none',
      lineStyle: { color: quadrantLineColor, type: 'dashed' },
      label: { show: false },
      data: [{ xAxis: meanX }, { yAxis: meanY }],
    };
    // 4 bands: [TR, TL, BL, BR] — colors from config; optional in-band captions
    // (Quadrant format group) shown via per-band `name` + markArea label.
    const qLabels = capabilities.quadrantLabels || [];
    const showQLabels = capabilities.showQuadrantLabels !== false && qLabels.some((l) => !!l);
    first.markArea = {
      silent: true,
      label: {
        show: showQLabels,
        position: 'inside',
        color: quadrantLabelColor,
        fontSize: quadrantLabelFontSize,
        fontWeight: 600,
      },
      data: [
        [{ name: qLabels[0] || '', coord: [meanX, meanY], itemStyle: { color: quadrantColors[0] } }, { coord: ['max', 'max'] }],
        [{ name: qLabels[1] || '', coord: ['min', meanY], itemStyle: { color: quadrantColors[1] } }, { coord: [meanX, 'max'] }],
        [{ name: qLabels[2] || '', coord: ['min', 'min'], itemStyle: { color: quadrantColors[2] } }, { coord: [meanX, meanY] }],
        [{ name: qLabels[3] || '', coord: [meanX, 'min'], itemStyle: { color: quadrantColors[3] } }, { coord: ['max', meanY] }],
      ],
    };
  }

  // ── Regression overlay: OLS trend line + optional confidence band + R² text.
  //    Multi Scatter gets ONE TRENDLINE PER SERIES (matching colors) with a
  //    per-series R² list; single-series charts get the global line + band. ──
  const xyPairs = allPts.map((p) => [p.value[0], p.value[1]] as [number, number]);
  /** A series' [x, y] pairs regardless of plain-array vs {value, srcRow} items. */
  const seriesValuePts = (s: SbSeries): Array<[number, number]> =>
    s.data.map((item) => (Array.isArray(item) ? item : item.value)).map((v) => [v[0], v[1]] as [number, number]);
  const isPerSeries = !!seriesField && seriesList.length > 1;
  if (isRegression && xyPairs.length >= 2 && isPerSeries) {
    const eqLines: string[] = [];
    seriesList.forEach((s, i) => {
      const pts = seriesValuePts(s);
      const fit = linearFit(pts);
      if (!fit) return;
      const xs = pts.map((p) => p[0]);
      const lo = Math.min(...xs);
      const hi = Math.max(...xs);
      extraSeries.push({
        type: 'line',
        silent: true,
        z: 3,
        symbol: 'none',
        data: [[lo, fit.a + fit.b * lo], [hi, fit.a + fit.b * hi]],
        lineStyle: { color: palette[i % palette.length], width: regressionLineWidth },
        tooltip: { show: false },
      });
      eqLines.push(`${s.name}: R² = ${Math.max(0, Math.min(1, fit.r2)).toFixed(2)}`);
    });
    if (capabilities.showEquation !== false && eqLines.length) {
      graphics.push({
        type: 'text',
        right: gridRight + statInsetX,
        top: gridTop + statInsetY,
        silent: true,
        style: { text: eqLines.join('\n'), fill: statTextColor, fontSize: statFontSize, fontWeight: 600, lineHeight: statFontSize + statLineGap },
      });
    }
  } else if (isRegression && xyPairs.length >= 2) {
    const fit = linearFit(xyPairs);
    if (fit) {
      const xs = xyPairs.map((p) => p[0]);
      const xLo = Math.min(...xs);
      const xHi = Math.max(...xs);
      extraSeries.push({
        type: 'line',
        silent: true,
        z: 3,
        symbol: 'none',
        data: [[xLo, fit.a + fit.b * xLo], [xHi, fit.a + fit.b * xHi]],
        lineStyle: { color: regressionLineColor, width: regressionLineWidth },
        tooltip: { show: false },
      });
      if (capabilities.showConfidenceBand !== false && fit.n > 2) {
        // Mean-prediction band: ŷ ± z·√(s²·(1/n + (x−x̄)²/Sxx)) (z=1.96 ≈ 95%),
        // rendered as the classic stacked-line trick (transparent lower + shaded delta).
        const s2 = (fit.syy * (1 - fit.r2)) / (fit.n - 2);
        const steps = regressionBandSteps;
        const lower: number[][] = [];
        const delta: number[][] = [];
        for (let i = 0; i <= steps; i++) {
          const x = xLo + ((xHi - xLo) * i) / steps;
          const half = confidenceZ * Math.sqrt(Math.max(0, s2 * (1 / fit.n + ((x - fit.meanX) ** 2) / fit.sxx)));
          lower.push([x, fit.a + fit.b * x - half]);
          delta.push([x, 2 * half]);
        }
        const bandStack = 'sb-regression-band';
        extraSeries.push(
          { type: 'line', silent: true, z: 2, symbol: 'none', stack: bandStack, data: lower, lineStyle: { opacity: 0 }, tooltip: { show: false } },
          { type: 'line', silent: true, z: 2, symbol: 'none', stack: bandStack, data: delta, lineStyle: { opacity: 0 }, areaStyle: { color: regressionLineColor, opacity: confidenceBandOpacity }, tooltip: { show: false } },
        );
      }
      if (capabilities.showEquation !== false) {
        const sign = fit.b >= 0 ? '+' : '−';
        graphics.push({
          type: 'text',
          right: gridRight + statInsetX,
          top: gridTop + statInsetY,
          silent: true,
          style: {
            text: `y = ${fit.a.toFixed(2)} ${sign} ${Math.abs(fit.b).toFixed(2)}·x    R² = ${Math.max(0, Math.min(1, fit.r2)).toFixed(2)}`,
            fill: statTextColor,
            fontSize: statFontSize,
            fontWeight: 600,
          },
        });
      }
    }
  }

  // ── Correlation overlay: coefficient text + color-intensity gradient legend
  //    (visualMap on the size dimension for Bubble, else on Y). ──
  let visualMap: Record<string, unknown> | undefined;
  if (isCorrelation && xyPairs.length >= 2) {
    const method = capabilities.correlationMethod === 'spearman' ? 'spearman' : 'pearson';
    const methodLabel = method === 'spearman' ? 'Spearman' : 'Pearson';
    const computeR = (pts: Array<[number, number]>): number => {
      if (method === 'spearman') {
        const rx = ranks(pts.map((p) => p[0]));
        const ry = ranks(pts.map((p) => p[1]));
        return pearson(rx.map((v, i) => [v, ry[i]] as [number, number]));
      }
      return pearson(pts);
    };
    if (capabilities.showCoefficient !== false) {
      // Multi Scatter: overall r + one r per series; single: just the overall.
      const lines = [`r = ${computeR(xyPairs).toFixed(2)} (${methodLabel})`];
      if (isPerSeries) seriesList.forEach((s) => lines.push(`${s.name}: r = ${computeR(seriesValuePts(s)).toFixed(2)}`));
      graphics.push({
        type: 'text',
        right: gridRight + statInsetX,
        top: gridTop + statInsetY,
        silent: true,
        style: {
          text: lines.join('\n'),
          fill: statTextColor,
          fontSize: statFontSize,
          fontWeight: 600,
          lineHeight: statFontSize + statLineGap,
        },
      });
    }
    // Intensity gradient only when ONE series owns the canvas — on Multi
    // Scatter it would override the per-series colors and orphan the legend.
    if (!isPerSeries) {
      const dim = sizeDimActive ? 2 : 1;
      const dimValues = allPts.map((p) => p.value[dim] ?? 0);
      visualMap = {
        type: 'continuous',
        dimension: dim,
        min: Math.min(...dimValues),
        max: Math.max(...dimValues),
        seriesIndex: seriesList.map((_, i) => i),
        inRange: { color: [gradientLow, gradientHigh] },
        calculable: false,
        right: 0,
        top: 'middle',
        itemHeight: Number(vmStyle.itemHeight),
        itemWidth: Number(vmStyle.itemWidth),
        text: [String(vmStyle.textHigh), String(vmStyle.textLow)],
        textStyle: { fontSize: Number(vmStyle.fontSize), color: statTextColor },
      };
    }
  }

  // ── Config-declared reference lines (any axis). Attach to the first series. ──
  const refLines = (capabilities.referenceLines || []).filter((r) => Number.isFinite(Number(r.value)));
  if (refLines.length && seriesList.length && !isQuadrant) {
    const first = seriesList[0] as SbSeries;
    first.markLine = {
      silent: true,
      symbol: 'none',
      lineStyle: { color: referenceLineColor, type: 'dashed' },
      label: { formatter: (p: { name?: string }) => p.name || '' },
      data: refLines.map((r) => (r.axis === 'y' ? { yAxis: Number(r.value), name: r.label } : { xAxis: Number(r.value), name: r.label })),
    };
  }

  // ── Axes (both numeric 'value'). ──
  const showGrid = capabilities.showGridLines !== false;
  const xLabel = capabilities.xAxisLabel || capabilities.xLabel || xField;
  const yLabel = capabilities.yAxisLabel || capabilities.yLabel || yField;
  // Year-like axes get plain "2024" ticks (no separators/decimals) and integer
  // steps — half-year ticks like "2,024.5" are meaningless for years.
  const xIsYear = !isMatrix && isYearDimension(allPts.map((p) => p.value[0]));
  const yIsYear = !isMatrix && isYearDimension(allPts.map((p) => p.value[1]));
  const xNumFmt = (v: number) => (xIsYear ? String(Math.round(v)) : formatValue(v, capabilities));
  const yNumFmt = (v: number) => (yIsYear ? String(Math.round(v)) : formatValue(v, capabilities));

  // Category label angle — only the matrix has a categorical X axis; numeric
  // axes ignore the setting. 'auto' mirrors Bar & Column: rotate 30° once the
  // category count crosses rotateThreshold, else keep labels flat.
  const rotateThreshold = d.rotateThreshold ?? FALLBACK.rotateThreshold;
  const rotationSetting = capabilities.axisLabelRotation || 'auto';
  const catRotate = rotationSetting === 'auto'
    ? (xCats.length > rotateThreshold ? (d.autoRotateDegrees ?? FALLBACK.autoRotateDegrees) : 0)
    : Number(rotationSetting) || 0;

  // Matrix → categorical grid axes (distinct sorted values, grid lines between
  // cells); scatter/bubble → continuous numeric axes.
  const xAxis: Record<string, unknown> = isMatrix
    ? {
        type: 'category',
        data: xCats,
        name: toCapitalCase(xLabel || ''),
        nameLocation: 'middle',
        nameGap: valueNameGap,
        boundaryGap: true,
        splitLine: { show: showGrid },
        axisLabel: { hideOverlap: true, rotate: catRotate },
      }
    : {
        type: 'value',
        name: toCapitalCase(xLabel || ''),
        nameLocation: 'middle',
        nameGap: valueNameGap,
        scale: true,
        splitNumber: valueAxisTicks,
        splitLine: { show: showGrid },
        axisLabel: { formatter: xNumFmt, hideOverlap: true },
        ...(xIsYear ? { minInterval: 1 } : {}),
        ...(capabilities.xAxisMin !== undefined ? { min: capabilities.xAxisMin } : {}),
        ...(capabilities.xAxisMax !== undefined ? { max: capabilities.xAxisMax } : {}),
      };
  const yAxis: Record<string, unknown> = isMatrix
    ? {
        type: 'category',
        data: yCats,
        name: toCapitalCase(yLabel || ''),
        nameLocation: 'end',
        nameGap: endNameGap,
        nameTextStyle: { align: 'left' as const },
        boundaryGap: true,
        splitLine: { show: showGrid },
        axisLabel: { hideOverlap: true },
      }
    : {
        type: 'value',
        name: toCapitalCase(yLabel || ''),
        nameLocation: 'end',
        nameGap: endNameGap,
        nameTextStyle: { align: 'left' as const },
        scale: true,
        splitNumber: valueAxisTicks,
        splitLine: { show: showGrid },
        axisLabel: { formatter: yNumFmt, hideOverlap: true },
        ...(yIsYear ? { minInterval: 1 } : {}),
        ...(capabilities.valueAxisMin !== undefined ? { min: capabilities.valueAxisMin } : {}),
        ...(capabilities.valueAxisMax !== undefined ? { max: capabilities.valueAxisMax } : {}),
      };

  // ── Tooltip. ──
  const tooltipTrigger = capabilities.tooltipTrigger || 'item';
  const tooltipOff = capabilities.tooltipEnabled === false || tooltipTrigger === 'none';
  const resolvedTrigger = tooltipTrigger === 'auto' ? 'item' : tooltipTrigger;
  const tooltipPos = tooltipPositionFor(capabilities.tooltipPosition, { near: tooltipGapNear, far: tooltipGapFar, inset: tooltipFixedInset });
  const tooltip = tooltipOff
    ? { show: false }
    : {
        show: true,
        trigger: resolvedTrigger,
        axisPointer: { type: capabilities.tooltipAxisPointer && capabilities.tooltipAxisPointer !== 'none' ? capabilities.tooltipAxisPointer : 'cross' },
        ...(tooltipPos !== undefined ? { position: tooltipPos } : {}),
        formatter: (params: unknown): string => {
          const p = (Array.isArray(params) ? params[0] : params) as { seriesName?: string; value?: number[]; marker?: string; data?: { srcRow?: Row } };
          const v = p?.value || [];
          // Matrix cells hover with their category names; scatter with numbers.
          const xStr = isMatrix ? (xCats[Number(v[0])] ?? '') : xNumFmt(Number(v[0] ?? 0));
          const yStr = isMatrix ? (yCats[Number(v[1])] ?? '') : yNumFmt(Number(v[1] ?? 0));
          const head = p?.seriesName ? `${p.marker ?? ''}${p.seriesName}<br/>` : '';
          const sizeStr = sizeDimActive && v[2] !== undefined ? `<br/>${toCapitalCase(sizeField || 'Count')} : ${formatValue(Number(v[2]), capabilities)}` : '';
          // Extra tooltip columns — raw values off the point's source row.
          const src = p?.data?.srcRow;
          const extras = src
            ? tooltipCols
                .map((col) => {
                  const raw = src[col];
                  const shown = typeof raw === 'number' ? formatValue(raw, capabilities) : String(raw ?? '');
                  return `<br/>${toCapitalCase(col)} : ${shown}`;
                })
                .join('')
            : '';
          return `${head}${toCapitalCase(xLabel)} : ${xStr}<br/>${toCapitalCase(yLabel)} : ${yStr}${sizeStr}${extras}`;
        },
        ...(capabilities.tooltipBackground ? { backgroundColor: capabilities.tooltipBackground } : {}),
        ...(capabilities.tooltipBorderColor ? { borderColor: capabilities.tooltipBorderColor } : {}),
        ...(capabilities.tooltipBorderRadius !== undefined ? { borderRadius: capabilities.tooltipBorderRadius } : {}),
        textStyle: {
          ...(capabilities.tooltipTextColor ? { color: capabilities.tooltipTextColor } : {}),
          ...(capabilities.tooltipFontSize ? { fontSize: capabilities.tooltipFontSize } : {}),
        },
      };

  // ── Legend. The showLegend toggle is authoritative (mirrors Bar & Column):
  //    a single-series chart's legend lists that one series — hiding it only
  //    because there is one entry made the "Show legend" toggle look broken. ──
  const legendPos = capabilities.legendPosition || 'bottom';
  const showLegend = seriesList.length > 0 && capabilities.showLegend !== false && legendPos !== 'none';
  const legend = showLegend
    ? {
        data: seriesList.map((s) => s.name),
        type: 'scroll' as const,
        ...(legendPos === 'top' ? { top: 0 } : legendPos === 'right' ? { right: legendRightOffset, orient: 'vertical' as const, top: 'middle' as const } : { bottom: 0 }),
      }
    : undefined;

  // ── Zoom (optional). ──
  const dataZoom = capabilities.enableZoom
    ? [
        { type: 'inside', xAxisIndex: 0 },
        { type: 'inside', yAxisIndex: 0 },
        ...(capabilities.showZoomSlider !== false ? [{ type: 'slider', xAxisIndex: 0 }] : []),
      ]
    : undefined;

  const bottomLegend = showLegend && legendPos === 'bottom';
  // The X-axis title renders at nameGap below the axis line — OUTSIDE what
  // containLabel reserves (it only measures tick labels), so give it its own
  // bottom band or it clips off the tile edge.
  const hasBottomAxisName = !!toCapitalCase(xLabel || '');

  return {
    color: palette,
    animation: capabilities.animation !== false,
    tooltip: tooltip as unknown as EChartsOption['tooltip'],
    legend,
    ...(dataZoom ? { dataZoom: dataZoom as unknown as EChartsOption['dataZoom'] } : {}),
    ...(visualMap ? { visualMap: visualMap as unknown as EChartsOption['visualMap'] } : {}),
    ...(graphics.length ? { graphic: graphics as unknown as EChartsOption['graphic'] } : {}),
    grid: {
      left: gridLeft,
      // Right-docked legend and the correlation visualMap gradient each need a
      // reserved gutter so they don't overlap the plot.
      right: (showLegend && legendPos === 'right' ? gridRightLegend : gridRight) + (visualMap ? visualMapGutter : 0),
      top: showLegend && legendPos === 'top' ? gridTopLegend : gridTop,
      bottom: gridBottomBase
        + (bottomLegend ? gridBottomLegend : 0)
        + (hasBottomAxisName ? gridBottomAxisName : 0),
      containLabel: true,
    },
    xAxis: xAxis as unknown as EChartsOption['xAxis'],
    yAxis: yAxis as unknown as EChartsOption['yAxis'],
    // Overlay series (trend line / confidence band / cluster hulls) ride along
    // AFTER the point series so legend + visualMap seriesIndex stay aligned.
    series: [...seriesList, ...extraSeries] as unknown as EChartsOption['series'],
  };
}
